import { useState } from "react";
import { Loader2, Plus, X, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, formatApiError } from "@/lib/api";
import { MEMBER_INTERESTS, INTENT_OPTIONS } from "@/lib/membership";
import { useMemberAuth } from "@/context/MemberAuthContext";

function ProfilePhotoWidget({ member }) {
  const { refresh } = useMemberAuth();
  const [uploading, setUploading] = useState(false);
  const [broken, setBroken] = useState(false);
  const photo = member.profile_photo;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("The photo must be under 2MB."); return; }
    setUploading(true);
    setBroken(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/member/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Profile photo updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not upload the photo.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try { await api.delete("/member/profile-photo"); await refresh(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Profile Photo</label>
      <p className="text-xs text-ptf-text/55 mb-3">JPEG, PNG or WebP, under 2MB. Automatically cropped to 500&times;500px.</p>
      {photo?.id ? (
        <div className="relative border border-ptf-primary/12 bg-white p-4 flex items-center gap-4 max-w-sm" data-testid="member-profile-photo-uploaded">
          {broken ? (
            <div className="h-24 w-24 grid place-items-center bg-ptf-primary/5 text-ptf-text/30 shrink-0"><ImageOff className="h-6 w-6" /></div>
          ) : (
            <img src={resolveImg(photo.url)} alt="Profile" onError={() => setBroken(true)} className="h-24 w-24 object-cover shrink-0" />
          )}
          <button type="button" onClick={remove} data-testid="member-profile-photo-delete" className="ml-auto bg-ptf-text/80 text-white p-1.5 hover:bg-red-600 transition-colors self-start"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label data-testid="member-profile-photo-upload" className="border-2 border-dashed border-ptf-primary/25 h-28 w-28 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-ptf-primary/50 transition-colors text-ptf-text/50">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
          <span className="text-[0.6rem] uppercase tracking-[0.12em]">Add Photo</span>
          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-ptf-primary/25 py-3 text-base outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";

const FIELDS = [
  ["name", "Full Name *", "text"], ["phone", "Phone *", "tel"],
  ["address", "Address", "text"], ["city", "City *", "text"],
  ["state", "State *", "text"], ["country", "Country *", "text"],
  ["education_qualification", "Education Qualification", "text"], ["field_of_education", "Field of Education", "text"],
];

const TEXTAREAS = [
  ["prior_experience", "Prior Experience"], ["relevant_skills", "Relevant Skills"],
  ["outdoor_field_experience", "Outdoor / Field Experience"], ["conservation_experience", "Conservation Experience"],
  ["photography_film_experience", "Photography / Film Experience"],
];

export default function ProfileForm({ member, onSaved, mode = "onboarding" }) {
  const [form, setForm] = useState({
    name: member.name || "", phone: member.phone || "", address: member.address || "",
    city: member.city || "", state: member.state || "", country: member.country || "India",
    education_qualification: member.education_qualification || "", field_of_education: member.field_of_education || "",
    prior_experience: member.prior_experience || "", relevant_skills: member.relevant_skills || "",
    outdoor_field_experience: member.outdoor_field_experience || "", conservation_experience: member.conservation_experience || "",
    photography_film_experience: member.photography_film_experience || "",
    interests: member.interests || [], interests_other: member.interests_other || "", intent: member.intent || "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInterest = (i) => setForm((f) => ({
    ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.intent) { toast.error("Please select what you'd like to do."); return; }
    setLoading(true);
    try {
      const { data } = await api.put("/member/profile", form);
      toast.success("Profile saved.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save profile.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="member-profile-form" className="bg-white border border-ptf-primary/10 p-7 md:p-10 space-y-8">
      <div>
        {mode === "onboarding" ? (
          <>
            <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50 mb-1">Step 1 of 3</p>
            <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">Complete Your Profile</h2>
          </>
        ) : (
          <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">My Profile</h2>
        )}
        <p className="mt-2 text-sm text-ptf-text/60">This helps us match you to relevant Training and Volunteer opportunities.</p>
      </div>

      <ProfilePhotoWidget member={member} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {FIELDS.map(([key, label, type]) => (
          <div key={key}>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">{label}</label>
            <input required={label.includes("*")} type={type} data-testid={`profile-${key}`} value={form[key]} onChange={set(key)} className={inputCls} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {TEXTAREAS.map(([key, label]) => (
          <div key={key}>
            <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">{label}</label>
            <textarea rows={2} data-testid={`profile-${key}`} value={form[key]} onChange={set(key)} className={`${inputCls} resize-none`} />
          </div>
        ))}
      </div>

      <div>
        <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">Areas of Interest</label>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MEMBER_INTERESTS.map((i) => (
            <label key={i} className="flex items-center gap-2.5 text-sm text-ptf-text/80 cursor-pointer">
              <input type="checkbox" data-testid={`interest-${i.split(" ")[0].toLowerCase()}`} checked={form.interests.includes(i)} onChange={() => toggleInterest(i)} className="h-4 w-4 accent-ptf-primary" />
              {i}
            </label>
          ))}
        </div>
        <input data-testid="profile-interests-other" value={form.interests_other} onChange={set("interests_other")} className={`${inputCls} mt-3`} placeholder="Other / specify" />
      </div>

      <div>
        <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/60">I want to: *</label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {INTENT_OPTIONS.map((opt) => (
            <button
              key={opt.value} type="button" data-testid={`profile-intent-${opt.value.toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, intent: opt.value }))}
              className={`border px-3 py-2.5 text-xs uppercase tracking-[0.1em] transition-colors ${form.intent === opt.value ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary/50"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <button type="submit" data-testid="profile-submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-ptf-primary text-ptf-bg px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-secondary transition-colors disabled:opacity-60">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? "Saving" : mode === "onboarding" ? "Save & Continue" : "Save Changes"}
      </button>
    </form>
  );
}
